<template>
  <UITooltip :disabled="!$slots.tooltip" :position="tooltipPosition">
    <template #activator="{ props: activatorProps }">
      <v-btn
        :disabled="disabled"
        :color="btnColor"
        :loading="loading"
        variant="text"
        :active="active"
        :class="classes"
        :type="type"
        :data-test-id="dataTestId"
        :size="btnSize"
        :to="to"
        icon
        rounded="circle"
        v-bind="{ ...activatorProps, ...$attrs }"
      >
        <slot name="icon">
          <v-icon v-if="icon" size="small">{{ icon }}</v-icon>
        </slot>

        <slot />
      </v-btn>
    </template>
    <template #default>
      <slot name="tooltip" />
    </template>
  </UITooltip>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import type { IIconSize } from '../ui-button/ui-button-types';
import type { ITooltipPosition } from '../ui-tooltip/tooltip-types';
import UITooltip from '../ui-tooltip/UITooltip.vue';

interface Props {
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  active?: boolean;
  type?: 'submit' | 'reset' | 'button';
  variant?: 'primary' | 'secondary' | 'black' | 'danger' | 'warning';
  iconSize?: IIconSize;
  size?: 'sm' | 'md';
  depressed?: boolean;
  tooltipPosition?: ITooltipPosition;
  dataTestId?: string;
  to?: RouteLocationRaw;
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  loading: false,
  disabled: false,
  depressed: false,
  variant: 'primary',
  type: 'button',
  iconSize: 'md',
  size: 'md',
  tooltipPosition: undefined,
  dataTestId: undefined,
  to: undefined,
});

const btnSize = computed(() => (props.size === 'sm' ? '24' : '36'));

const btnColor = computed(() => {
  if (props.variant === 'secondary') return 'primary';
  if (props.variant === 'danger') return 'error';
  return props.variant;
});

const classes = computed(() => {
  const nextClasses: string[] = [];

  if (props.variant === 'secondary')
    nextClasses.push('bg-primary', 'text-white');
  if (props.active) nextClasses.push('secondary');
  if (props.iconSize === 'lg') nextClasses.push('text-lg');
  if (props.iconSize === 'xl') nextClasses.push('text-xl');

  return nextClasses;
});
</script>

<style scoped lang="scss">
.icon-lg {
  :deep(.v-icon) {
    font-size: 1.5em !important;
  }
}
</style>
